/**
 * <EntityDrawer /> — right-slide drawer for related-entity peeks,
 * audit/trace surfaces, and detail-from-list views.
 *
 * Per DESIGN_SYSTEM.md §7 "Component Decision Rules":
 *   - Drawer for detail views opened from a list, related-item
 *     peeks, AuditTraceViewer, audit log inspection, AI activity
 *     detail.
 *   - Drawer preserves the page underneath (vs modal which blocks).
 *   - Esc closes; focus trapped; "Open in full page" link exposed
 *     for users who want to navigate.
 *
 * Built on Radix Dialog for focus trap + ARIA semantics. Slides
 * from the right in LTR, from the left in RTL (the `slideInRight`
 * variant is flipped via the `dir` prop the parent <html> carries).
 *
 * Motion: framer-motion's drawerEnter/Exit transitions, respects
 * prefers-reduced-motion via withReducedMotion().
 */

import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { ExternalLink, X } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { TRANSITION, withReducedMotion } from "../../lib/motion";

export interface EntityDrawerProps {
  /** Controls open / close. */
  open: boolean;
  /** Called when Radix decides the drawer should close (Esc, overlay click). */
  onOpenChange: (open: boolean) => void;
  /** Heading rendered in the drawer header. */
  title: string;
  /** Optional one-line description below the title. */
  description?: string;
  /** Drawer body content. */
  children: ReactNode;
  /** Optional footer area (action buttons). */
  footer?: ReactNode;
  /** "Open in full page" link href. Hidden if omitted. */
  openInFullPageHref?: string;
  /** Width preset. Default "md" (480px). */
  width?: "sm" | "md" | "lg";
  /** Optional className passthrough on the drawer panel. */
  className?: string;
}

const WIDTH_CLASS: Record<NonNullable<EntityDrawerProps["width"]>, string> = {
  sm: "max-w-[380px]",
  md: "max-w-[480px]",
  lg: "max-w-[640px]",
};

export function EntityDrawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  openInFullPageHref,
  width = "md",
  className = "",
}: EntityDrawerProps): ReactNode {
  const { t } = useTranslation();

  // Detect RTL at render time so the drawer slides from the correct
  // side. We read the html dir attribute rather than the i18n locale
  // because tests + non-app surfaces may bypass i18next.
  const isRtl =
    typeof document !== "undefined" &&
    document.documentElement.dir === "rtl";

  const slideInitial = isRtl ? { x: "-100%" } : { x: "100%" };
  const slideExit = isRtl ? { x: "-100%" } : { x: "100%" };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            {/* Overlay — a soft gray wash (NOT a heavy modal scrim)
                that puts focus on the drawer without making it feel
                like a floater. The 20% black is gentle enough that
                you can still read the page behind. Click / Esc
                close via Radix's overlay semantics. */}
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-[60]"
                style={{ backgroundColor: "rgba(15, 23, 42, 0.25)" }}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  transition: withReducedMotion(TRANSITION.drawerEnter),
                }}
                exit={{
                  opacity: 0,
                  transition: withReducedMotion(TRANSITION.drawerExit),
                }}
              />
            </Dialog.Overlay>

            {/* Drawer panel — flex column so header stays put,
                children scroll, footer pins to bottom. It docks at
                the end edge, offset by `--chat-panel-width` so it
                sits NEXT TO the open chat panel (or flush to the
                edge when the chat is closed) — never on top of it. */}
            <Dialog.Content asChild>
              <motion.div
                aria-describedby={description ? undefined : undefined}
                style={{
                  insetInlineEnd: "var(--chat-panel-width, 0px)",
                  // Explicit opaque background — `bg-surface-default`
                  // alone wasn't reliably opaque across all surfaces
                  // (the drawer was reading as see-through over the
                  // top bar). Same belt-and-braces fix as the AI
                  // status popover.
                  backgroundColor: "var(--surface-default, #ffffff)",
                }}
                className={[
                  "fixed top-0 bottom-0 z-[60]",
                  "w-full",
                  WIDTH_CLASS[width],
                  "flex flex-col",
                  "shadow-lg",
                  "border-s border-border-subtle",
                  className,
                ]
                  .filter(Boolean)
                  .join(" ")}
                initial={{ ...slideInitial, opacity: 0 }}
                animate={{
                  x: 0,
                  opacity: 1,
                  transition: withReducedMotion(TRANSITION.drawerEnter),
                }}
                exit={{
                  ...slideExit,
                  opacity: 0,
                  transition: withReducedMotion(TRANSITION.drawerExit),
                }}
              >
                <header className="flex shrink-0 items-start justify-between gap-3 border-b border-border-subtle px-5 py-4">
                  <div className="min-w-0 flex-1">
                    <Dialog.Title className="text-lg font-semibold text-text-primary">
                      {title}
                    </Dialog.Title>
                    {description && (
                      <Dialog.Description className="mt-1 text-sm text-text-secondary">
                        {description}
                      </Dialog.Description>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {openInFullPageHref && (
                      <a
                        href={openInFullPageHref}
                        className={[
                          "inline-flex items-center gap-1 rounded-sm",
                          "px-2 py-1 text-xs font-medium",
                          "text-text-secondary hover:text-text-primary",
                          "transition-colors duration-micro ease-out",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40",
                        ].join(" ")}
                      >
                        <ExternalLink size={12} />
                        {t("common.open_in_full_page")}
                      </a>
                    )}
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        aria-label={t("common.close")}
                        className={[
                          "inline-flex h-7 w-7 items-center justify-center",
                          "rounded-sm text-text-secondary",
                          "hover:bg-surface-sunken hover:text-text-primary",
                          "transition-colors duration-micro ease-out",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40",
                        ].join(" ")}
                      >
                        <X size={16} />
                      </button>
                    </Dialog.Close>
                  </div>
                </header>

                <div className="flex-1 overflow-y-auto px-5 py-4">
                  {children}
                </div>

                {footer && (
                  <footer className="shrink-0 border-t border-border-subtle px-5 py-3">
                    {footer}
                  </footer>
                )}
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
